import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MediaPicker } from '../components/forms/MediaPicker';
import { BlogDataEditor } from '../components/sections/BlogDataEditor';
import { PageHeader, ResponsiveTable } from '../components/ui/responsive-table';
import { usePages, usePageById, useUpdateSection } from '../hooks/usePages';
import { useMassages } from '../hooks/useMassages';
import { useSoins } from '../hooks/useSoins';
import { Pencil, Loader2, ChevronLeft, Save, Plus, X, Heart, Moon, Activity, Brain, Sparkles, Flower, Sun, Leaf, Smile, Zap, Wind, Droplet, Star, Shield, Waves, Circle, Feather, Eye, Flame, Music, CloudSun, Lightbulb, Users, MessageCircle } from 'lucide-react';
import type { Section } from '../types/entities';

type SectionDataValue = Record<string, unknown> | null;
type SectionDataEditorProps = { value: SectionDataValue; onChange: (data: SectionDataValue) => void };

const sectionFormSchema = z.object({
  type: z.string().min(1, 'Type requis'),
  title: z.string().nullable().optional(),
  content: z.string().nullable().optional(), // Optional for sections like ambiance
  data: z.any().nullable(), // Can be object or null
  imageUrl: z.string().nullable().optional(),
});

type SectionFormData = z.infer<typeof sectionFormSchema>;

export default function Pages() {
  const { data: pages, isLoading } = usePages();
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  if (selectedPageId) {
    return <PageEditor pageId={selectedPageId} onBack={() => setSelectedPageId(null)} />;
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Pages"
          description="Gérez le contenu de vos pages (titre, hero, sections)"
        />

        {/* Pages List */}
        <>
          {/* Mobile/Tablet: Card Layout */}
          <div className="lg:hidden space-y-4">
            {pages?.map((page) => (
              <div
                key={page.id}
                className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900">
                        {page.title}
                      </h3>
                      <p className="text-sm text-gray-500">/{page.slug}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Modifié le {new Date(page.updatedAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedPageId(page.id)}
                      className="flex-shrink-0"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Éditer
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table Layout */}
          <div className="hidden lg:block">
            <ResponsiveTable>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Page
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Slug
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Dernière modification
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pages?.map((page) => (
                    <tr key={page.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{page.title}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">/{page.slug}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(page.updatedAt).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedPageId(page.id)}
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Éditer
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ResponsiveTable>
          </div>
        </>
      </div>
    </AdminLayout>
  );
}

function PageEditor({ pageId, onBack }: { pageId: string; onBack: () => void }) {
  const { data: page, isLoading } = usePageById(pageId);
  const updateSection = useUpdateSection();
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);

  if (isLoading || !page) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header with back button */}
        <div className="flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="flex-shrink-0">
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">Retour</span>
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Éditer: {page.title}</h1>
            <p className="mt-1 sm:mt-2 text-gray-600 text-xs sm:text-sm">Slug: /{page.slug}</p>
          </div>
        </div>

        {/* Sections List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold mb-4">Sections ({page.sections.length})</h2>
          <div className="space-y-4">
            {page.sections.map((section) => (
              <SectionEditor
                key={section.id}
                section={section}
                isExpanded={editingSectionId === section.id}
                isDisabled={false}
                onToggle={() =>
                  setEditingSectionId(
                    editingSectionId === section.id ? null : section.id
                  )
                }
                onUpdate={async (data) => {
                  await updateSection.mutateAsync({
                    id: section.id,
                    data: {
                      type: data.type,
                      title: data.title ?? undefined,
                      content: data.content ?? undefined,
                      data: data.data,
                      imageUrl: data.imageUrl ?? undefined,
                    },
                  });
                  setEditingSectionId(null);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

// Editor for text_image sections (CTA text and link)
function TextImageDataEditor({ value, onChange }: SectionDataEditorProps) {
  const ctaText = (value?.ctaText as string) || '';
  const ctaLink = (value?.ctaLink as string) || '';
  const formation = (value?.formation as string) || '';
  const circular = (value?.circular as boolean) || false;

  return (
    <div className="space-y-4">
      {/* Formation (pour page À Propos - Mon Parcours) */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Formation / Citation</h3>
        <p className="text-xs text-gray-500 mb-3">
          Texte affiché dans un encadré crème avec bordure dorée (optionnel, utilisé sur la page À Propos)
        </p>

        <div className="space-y-2">
          <Label>Texte de la formation</Label>
          <Textarea
            value={formation}
            onChange={(e) => onChange({ ...value, formation: e.target.value })}
            placeholder="Ex: Diplômée en massage bien-être et réflexologie..."
            rows={3}
          />
        </div>
      </div>

      {/* CTA Button */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Bouton CTA (optionnel)</h3>

        <div className="space-y-2">
          <Label>Texte du bouton</Label>
          <Input
            type="text"
            value={ctaText}
            onChange={(e) => onChange({ ...value, ctaText: e.target.value })}
            placeholder="Découvrir mon parcours"
          />
        </div>

        <div className="space-y-2">
          <Label>Lien du bouton</Label>
          <Input
            type="text"
            value={ctaLink}
            onChange={(e) => onChange({ ...value, ctaLink: e.target.value })}
            placeholder="/a-propos ou https://..."
          />
        </div>
      </div>

      {/* Image Options */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Options d'image</h3>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="circular-image"
            checked={circular}
            onChange={(e) => onChange({ ...value, circular: e.target.checked })}
            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
          />
          <Label htmlFor="circular-image" className="cursor-pointer">
            Afficher l'image en cercle (pour photo de portrait)
          </Label>
        </div>
      </div>
    </div>
  );
}

// Editor for benefits sections (benefit cards)
interface BenefitCard {
  icon: string;
  title: string;
  description: string;
}

function BenefitsDataEditor({ value, onChange }: SectionDataEditorProps) {
  const cards = ((value?.cards as BenefitCard[]) || []);

  const availableIcons = [
    { value: 'Heart', label: 'Cœur', Icon: Heart },
    { value: 'Moon', label: 'Lune', Icon: Moon },
    { value: 'Activity', label: 'Activité', Icon: Activity },
    { value: 'Brain', label: 'Cerveau', Icon: Brain },
    { value: 'Sparkles', label: 'Étincelles', Icon: Sparkles },
    { value: 'Flower', label: 'Fleur', Icon: Flower },
    { value: 'Sun', label: 'Soleil', Icon: Sun },
    { value: 'Leaf', label: 'Feuille', Icon: Leaf },
    { value: 'Smile', label: 'Sourire', Icon: Smile },
    { value: 'Zap', label: 'Énergie', Icon: Zap },
    { value: 'Wind', label: 'Respiration', Icon: Wind },
    { value: 'Droplet', label: 'Eau', Icon: Droplet },
    { value: 'Star', label: 'Étoile', Icon: Star },
    { value: 'Shield', label: 'Protection', Icon: Shield },
    { value: 'Waves', label: 'Vagues', Icon: Waves },
    { value: 'Circle', label: 'Harmonie', Icon: Circle },
    { value: 'Feather', label: 'Légèreté', Icon: Feather },
    { value: 'Eye', label: 'Conscience', Icon: Eye },
    { value: 'Flame', label: 'Chaleur', Icon: Flame },
    { value: 'Music', label: 'Musique', Icon: Music },
    { value: 'CloudSun', label: 'Équilibre', Icon: CloudSun },
    { value: 'Lightbulb', label: 'Clarté', Icon: Lightbulb },
    { value: 'Users', label: 'Communauté', Icon: Users },
    { value: 'MessageCircle', label: 'Communication', Icon: MessageCircle },
  ];

  const updateCard = (index: number, field: keyof BenefitCard, val: string) => {
    const newCards = [...cards];
    newCards[index] = { ...newCards[index], [field]: val };
    onChange({ ...value, cards: newCards });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Cartes de bienfaits</Label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.map((card, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-white">
            <div className="flex items-center justify-center mb-2">
              <span className="text-sm font-medium text-gray-700">Carte #{index + 1}</span>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Icône</Label>
              <div className="grid grid-cols-6 gap-1.5">
                {availableIcons.slice(0, 12).map((iconItem) => {
                  const IconComponent = iconItem.Icon;
                  const isSelected = card.icon === iconItem.value;
                  return (
                    <button
                      key={iconItem.value}
                      type="button"
                      onClick={() => updateCard(index, 'icon', iconItem.value)}
                      className={`flex items-center justify-center p-1.5 border-2 rounded-md transition-all hover:border-[hsl(var(--spa-gold))] ${
                        isSelected
                          ? 'border-[hsl(var(--spa-gold))] bg-amber-50'
                          : 'border-gray-300 bg-white'
                      }`}
                      title={iconItem.label}
                    >
                      <IconComponent className={`w-4 h-4 ${isSelected ? 'text-[hsl(var(--spa-gold))]' : 'text-gray-600'}`} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`card-title-${index}`} className="text-xs">Titre</Label>
              <Input
                id={`card-title-${index}`}
                value={card.title}
                onChange={(e) => updateCard(index, 'title', e.target.value)}
                placeholder="Réduction du stress"
                className="text-sm h-9"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`card-description-${index}`} className="text-xs">Description</Label>
              <Textarea
                id={`card-description-${index}`}
                value={card.description}
                onChange={(e) => updateCard(index, 'description', e.target.value)}
                placeholder="Le massage favorise la libération d'endorphines..."
                rows={2}
                className="text-sm resize-none"
              />
            </div>
          </div>
        ))}
      </div>

      {cards.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">
          Aucune carte de bienfaits. Cliquez sur "Ajouter une carte" pour commencer.
        </p>
      )}
    </div>
  );
}

// Editor for prestations sections (featured services selection)
function PrestationsDataEditor({ value, onChange }: SectionDataEditorProps) {
  const { data: massages, isLoading: massagesLoading } = useMassages();
  const { data: soins, isLoading: soinsLoading } = useSoins();

  const featuredSlugs = ((value?.featuredSlugs as string[]) || []);
  const badges = ((value?.badges as Record<string, string>) || {});

  const allServices = [...(massages || []), ...(soins || [])];

  // Helper to get full MinIO URL
  const getImageUrl = (imageUrl: string | null | undefined): string => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `http://localhost:9000/cms/${imageUrl.replace(/^\//, '')}`;
  };

  const toggleService = (slug: string) => {
    let newSlugs: string[];
    if (featuredSlugs.includes(slug)) {
      newSlugs = featuredSlugs.filter(s => s !== slug);
    } else {
      if (featuredSlugs.length >= 3) {
        alert('Vous pouvez sélectionner maximum 3 services');
        return;
      }
      newSlugs = [...featuredSlugs, slug];
    }
    onChange({ ...value, featuredSlugs: newSlugs });
  };

  const setBadge = (slug: string, badge: string) => {
    onChange({
      ...value,
      badges: { ...badges, [slug]: badge }
    });
  };

  if (massagesLoading || soinsLoading) {
    return <div className="text-sm text-gray-500">Chargement des services...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Services mis en avant (sélectionnez 3 max)</Label>
        <span className="text-sm text-gray-500">{featuredSlugs.length}/3 sélectionnés</span>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
        {allServices.map((service) => {
          const isSelected = featuredSlugs.includes(service.slug);
          return (
            <div
              key={service.id}
              className={`flex items-center justify-between p-3 border rounded-md ${
                isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
              }`}
            >
              <label className="flex items-center gap-3 flex-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleService(service.slug)}
                  className="w-4 h-4 flex-shrink-0"
                />
                {service.imageUrl && (
                  <img
                    src={getImageUrl(service.imageUrl)}
                    alt={service.title}
                    className="w-16 h-16 object-cover rounded flex-shrink-0"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{service.title}</div>
                  <div className="text-sm text-gray-500">{service.slug}</div>
                </div>
              </label>

              {isSelected && (
                <select
                  value={badges[service.slug] || ''}
                  onChange={(e) => setBadge(service.slug, e.target.value)}
                  className="ml-4 text-sm border border-gray-300 rounded px-2 py-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="">Aucun badge</option>
                  <option value="popular">Populaire</option>
                  <option value="new">Nouveauté</option>
                  <option value="recommended">Recommandé</option>
                </select>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Editor for session process sections (step-by-step guide)
interface SessionStep {
  title: string;
  description: string;
}

// Approach Data Editor (3 dimensions with icons)
interface ApproachDimension {
  title: string;
  description: string;
  icon?: string;
}

function ApproachDataEditor({ value, onChange }: SectionDataEditorProps) {
  const dimensions = ((value?.dimensions as ApproachDimension[]) || []);

  const availableIcons = [
    { value: 'Heart', label: 'Cœur', Icon: Heart },
    { value: 'Sparkles', label: 'Étincelles', Icon: Sparkles },
    { value: 'Leaf', label: 'Feuille', Icon: Leaf },
    { value: 'Moon', label: 'Lune', Icon: Moon },
    { value: 'Activity', label: 'Activité', Icon: Activity },
    { value: 'Brain', label: 'Cerveau', Icon: Brain },
    { value: 'Flower', label: 'Fleur', Icon: Flower },
    { value: 'Sun', label: 'Soleil', Icon: Sun },
    { value: 'Smile', label: 'Sourire', Icon: Smile },
    { value: 'Zap', label: 'Énergie', Icon: Zap },
    { value: 'Wind', label: 'Respiration', Icon: Wind },
    { value: 'Droplet', label: 'Eau', Icon: Droplet },
  ];

  const updateDimension = (index: number, field: keyof ApproachDimension, val: string) => {
    const newDimensions = [...dimensions];
    newDimensions[index] = { ...newDimensions[index], [field]: val };
    onChange({ ...value, dimensions: newDimensions });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Dimensions de l'approche</Label>
      </div>
      <p className="text-sm text-gray-500">Sélectionnez une icône et personnalisez chaque dimension</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {dimensions.slice(0, 3).map((dimension, index) => {
          const defaultIcons = ['Heart', 'Sparkles', 'Leaf'];
          const selectedIcon = dimension.icon || defaultIcons[index];

          return (
            <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-white">
              <div className="flex items-center justify-center mb-2">
                <span className="text-sm font-medium text-gray-700">Dimension #{index + 1}</span>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Icône</Label>
                <div className="grid grid-cols-6 gap-1.5">
                  {availableIcons.map((iconItem) => {
                    const IconComponent = iconItem.Icon;
                    const isSelected = selectedIcon === iconItem.value;
                    return (
                      <button
                        key={iconItem.value}
                        type="button"
                        onClick={() => updateDimension(index, 'icon', iconItem.value)}
                        className={`flex items-center justify-center p-1.5 border-2 rounded-md transition-all hover:border-[hsl(var(--spa-gold))] ${
                          isSelected
                            ? 'border-[hsl(var(--spa-gold))] bg-amber-50'
                            : 'border-gray-300 bg-white'
                        }`}
                        title={iconItem.label}
                      >
                        <IconComponent className={`w-4 h-4 ${isSelected ? 'text-[hsl(var(--spa-gold))]' : 'text-gray-600'}`} />
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor={`dimension-title-${index}`} className="text-xs">Titre</Label>
                <Input
                  id={`dimension-title-${index}`}
                  value={dimension.title}
                  onChange={(e) => updateDimension(index, 'title', e.target.value)}
                  placeholder={`${index === 0 ? 'Physique' : index === 1 ? 'Émotionnelle' : 'Énergétique'}`}
                  className="text-sm h-9"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`dimension-desc-${index}`} className="text-xs">Description</Label>
                <Textarea
                  id={`dimension-desc-${index}`}
                  value={dimension.description}
                  onChange={(e) => updateDimension(index, 'description', e.target.value)}
                  rows={2}
                  placeholder="Description de cette dimension..."
                  className="text-sm resize-none"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SessionProcessDataEditor({ value, onChange }: SectionDataEditorProps) {
  const steps = ((value?.steps as SessionStep[]) || []);

  const updateStep = (index: number, field: keyof SessionStep, val: string) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: val };
    onChange({ ...value, steps: newSteps });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-base font-semibold">Étapes de la séance</Label>
        <p className="text-sm text-gray-500 mt-1">
          Modifiez le contenu des étapes. Le nombre d'étapes est fixe et ne peut être modifié.
        </p>
      </div>

      {steps.length === 0 && (
        <div className="rounded-md bg-amber-50 p-4">
          <p className="text-sm text-amber-800">
            <strong>Note :</strong> Aucune étape n'est définie pour cette section.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {steps.map((step, index) => (
          <div
            key={index}
            className="relative bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
          >
            {/* Step Number Badge */}
            <div className="absolute -top-3 -left-3 w-10 h-10 bg-[hsl(var(--spa-gold))] rounded-full flex items-center justify-center shadow-lg border-3 border-white">
              <span className="text-white font-bold text-base">{index + 1}</span>
            </div>

            {/* Content */}
            <div className="space-y-3 pt-2">
              <div className="space-y-1.5">
                <Label htmlFor={`step-title-${index}`} className="text-xs font-semibold text-gray-700">
                  Titre
                </Label>
                <Input
                  id={`step-title-${index}`}
                  value={step.title}
                  onChange={(e) => updateStep(index, 'title', e.target.value)}
                  placeholder="Ex: Accueil personnalisé"
                  className="border-gray-300 focus:border-[hsl(var(--spa-gold))] focus:ring-[hsl(var(--spa-gold))] h-9 text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor={`step-description-${index}`} className="text-xs font-semibold text-gray-700">
                  Description
                </Label>
                <Textarea
                  id={`step-description-${index}`}
                  value={step.description}
                  onChange={(e) => updateStep(index, 'description', e.target.value)}
                  placeholder="Décrivez cette étape..."
                  rows={3}
                  className="border-gray-300 focus:border-[hsl(var(--spa-gold))] focus:ring-[hsl(var(--spa-gold))] resize-none text-sm"
                />
                <div className="flex justify-end">
                  <span className="text-xs text-gray-400">
                    {step.description?.length || 0} car.
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Editor for Values section
interface ValueItem {
  title: string;
}

function ValuesDataEditor({ value, onChange }: SectionDataEditorProps) {
  const values = ((value?.values as ValueItem[]) || []);

  const updateValue = (index: number, field: keyof ValueItem, val: string) => {
    const newValues = [...values];
    newValues[index] = { ...newValues[index], [field]: val };
    onChange({ ...value, values: newValues });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-base font-semibold">Valeurs</Label>
        <p className="text-sm text-gray-500 mt-1">
          Les valeurs s'affichent en grille 2 colonnes avec une icône checkmark dorée. Modifiez le contenu des valeurs. Le nombre de valeurs est fixe et ne peut être modifié.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {values.map((item, index) => (
          <div key={index} className="relative bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="text-[hsl(var(--spa-gold))] mt-1 text-xl flex-shrink-0">✓</span>

              <div className="flex-1">
                <div className="space-y-1.5">
                  <Label htmlFor={`value-title-${index}`}>Valeur #{index + 1}</Label>
                  <Input
                    id={`value-title-${index}`}
                    value={item.title}
                    onChange={(e) => updateValue(index, 'title', e.target.value)}
                    placeholder="Ex: Respect et bienveillance"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Editor for FAQ sections (questions & answers)
interface FAQItem {
  question: string;
  answer: string;
}

function FAQDataEditor({ value, onChange }: SectionDataEditorProps) {
  const faqs = ((value?.faqs as FAQItem[]) || []);

  const addFAQ = () => {
    onChange({
      ...value,
      faqs: [...faqs, { question: '', answer: '' }],
    });
  };

  const removeFAQ = (index: number) => {
    onChange({
      ...value,
      faqs: faqs.filter((_, i) => i !== index),
    });
  };

  const updateFAQ = (index: number, field: keyof FAQItem, val: string) => {
    const newFAQs = [...faqs];
    newFAQs[index] = { ...newFAQs[index], [field]: val };
    onChange({ ...value, faqs: newFAQs });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Questions & Réponses</Label>
        <Button type="button" onClick={addFAQ} size="sm" variant="outline" className="gap-2">
          <Plus className="w-4 h-4" />
          Ajouter une question
        </Button>
      </div>

      {faqs.map((faq, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Question #{index + 1}</span>
            <Button
              type="button"
              onClick={() => removeFAQ(index)}
              size="sm"
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`faq-question-${index}`}>Question</Label>
            <Input
              id={`faq-question-${index}`}
              value={faq.question}
              onChange={(e) => updateFAQ(index, 'question', e.target.value)}
              placeholder="Comment se déroule un soin ?"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`faq-answer-${index}`}>Réponse</Label>
            <Textarea
              id={`faq-answer-${index}`}
              value={faq.answer}
              onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
              placeholder="Vous restez habillé(e) et vous vous installez..."
              rows={4}
            />
          </div>
        </div>
      ))}

      {faqs.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">
          Aucune question définie. Cliquez sur "Ajouter une question" pour commencer.
        </p>
      )}
    </div>
  );
}

// Editor for Ambiance Band section (quote on image)
function AmbianceDataEditor({ value, onChange }: SectionDataEditorProps) {
  const quote = (value?.quote as string) || '';
  const overlayOpacity = (value?.overlayOpacity as number) || 60;

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-amber-50 p-4 mb-4">
        <p className="text-sm text-amber-800">
          <strong>Bande Visuelle :</strong> Une image pleine largeur avec une citation inspirante et un overlay doré.
        </p>
      </div>

      {/* Quote */}
      <div className="space-y-2">
        <Label htmlFor="ambiance-quote">Citation</Label>
        <Textarea
          id="ambiance-quote"
          value={quote}
          onChange={(e) => onChange({ ...value, quote: e.target.value })}
          placeholder="Un moment de paix pour votre corps et votre esprit"
          rows={3}
        />
        <p className="text-xs text-gray-500">
          La citation sera affichée en blanc, centrée sur l'image.
        </p>
      </div>

      {/* Overlay Opacity */}
      <div className="space-y-2">
        <Label htmlFor="ambiance-opacity">Opacité de l'overlay ({overlayOpacity}%)</Label>
        <input
          type="range"
          id="ambiance-opacity"
          min="30"
          max="80"
          value={overlayOpacity}
          onChange={(e) => onChange({ ...value, overlayOpacity: parseInt(e.target.value) })}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[hsl(var(--spa-gold))]"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>Plus clair (30%)</span>
          <span>Plus foncé (80%)</span>
        </div>
      </div>
    </div>
  );
}

// Editor for Mosaic Gallery section
function MosaicGalleryDataEditor({ value, onChange }: SectionDataEditorProps) {
  const images = ((value?.images as string[]) || []);
  const layout = (value?.layout as string) || 'row';

  const updateImage = (index: number, url: string) => {
    const newImages = [...images];
    newImages[index] = url;
    onChange({ ...value, images: newImages });
  };

  const addImage = () => {
    const maxImages = layout === 'mosaic' ? 3 : 4;
    if (images.length >= maxImages) {
      alert(`Maximum ${maxImages} images pour ce layout`);
      return;
    }
    onChange({
      ...value,
      images: [...images, ''],
    });
  };

  const removeImage = (index: number) => {
    onChange({
      ...value,
      images: images.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-green-50 p-4 mb-4">
        <p className="text-sm text-green-800">
          <strong>Galerie Mosaïque :</strong> Affiche des images d'ambiance de votre cabinet.
          {layout === 'mosaic' ? ' Layout asymétrique avec 3 images.' : ' Layout en ligne avec 4 images.'}
        </p>
      </div>

      {/* Layout Selection */}
      <div className="space-y-2">
        <Label>Type de disposition</Label>
        <div className="flex gap-4">
          <label
            className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
              layout === 'row'
                ? 'border-[hsl(var(--spa-gold))] bg-amber-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="layout"
              value="row"
              checked={layout === 'row'}
              onChange={(e) => onChange({ ...value, layout: e.target.value })}
              className="sr-only"
            />
            <div className="text-center">
              <div className="font-medium">En ligne</div>
              <div className="text-xs text-gray-500 mt-1">4 images côte à côte</div>
              <div className="mt-2 flex gap-1 justify-center">
                <div className="w-6 h-4 bg-gray-300 rounded"></div>
                <div className="w-6 h-4 bg-gray-300 rounded"></div>
                <div className="w-6 h-4 bg-gray-300 rounded"></div>
                <div className="w-6 h-4 bg-gray-300 rounded"></div>
              </div>
            </div>
          </label>

          <label
            className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
              layout === 'mosaic'
                ? 'border-[hsl(var(--spa-gold))] bg-amber-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="layout"
              value="mosaic"
              checked={layout === 'mosaic'}
              onChange={(e) => onChange({ ...value, layout: e.target.value })}
              className="sr-only"
            />
            <div className="text-center">
              <div className="font-medium">Mosaïque</div>
              <div className="text-xs text-gray-500 mt-1">1 grande + 2 petites</div>
              <div className="mt-2 flex gap-1 justify-center">
                <div className="w-8 h-8 bg-gray-300 rounded"></div>
                <div className="flex flex-col gap-1">
                  <div className="w-4 h-4 bg-gray-300 rounded"></div>
                  <div className="w-4 h-4 bg-gray-300 rounded"></div>
                </div>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Images List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Images ({images.length}/{layout === 'mosaic' ? 3 : 4})</Label>
          <Button
            type="button"
            onClick={addImage}
            size="sm"
            variant="outline"
            className="gap-2"
            disabled={images.length >= (layout === 'mosaic' ? 3 : 4)}
          >
            <Plus className="w-4 h-4" />
            Ajouter
          </Button>
        </div>

        {/* Grid 2x2 sur desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {images.map((imageUrl, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
              {/* Header avec titre et bouton supprimer */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Image #{index + 1}
                  {layout === 'mosaic' && index === 0 && ' (Grande)'}
                </span>
                <Button
                  type="button"
                  onClick={() => removeImage(index)}
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Sélecteur d'image depuis la galerie */}
              <MediaPicker
                value={imageUrl}
                onChange={(url) => updateImage(index, url || '')}
                label=""
              />
            </div>
          ))}
        </div>

        {images.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-8 border border-dashed border-gray-300 rounded-lg">
            Aucune image. Cliquez sur "Ajouter" pour commencer.
          </p>
        )}
      </div>
    </div>
  );
}

function SectionEditor({
  section,
  isExpanded,
  isDisabled = false,
  onToggle,
  onUpdate,
}: {
  section: Section;
  isExpanded: boolean;
  isDisabled?: boolean;
  onToggle: () => void;
  onUpdate: (data: SectionFormData) => Promise<void>;
}) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SectionFormData>({
    resolver: zodResolver(sectionFormSchema),
    defaultValues: {
      type: section.type,
      title: section.title,
      content: section.content,
      data: section.data || null,
      imageUrl: section.imageUrl,
    },
  });

  const imageUrl = watch('imageUrl');
  const sectionType = watch('type');
  const sectionData = watch('data');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data: SectionFormData) => {
    setIsSubmitting(true);
    try {
      await onUpdate(data);
    } catch (error) {
      console.error('Error submitting section:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`border rounded-lg ${isDisabled ? 'border-gray-300 bg-gray-50 opacity-60' : 'border-gray-200'}`}>
      <button
        onClick={isDisabled ? undefined : onToggle}
        disabled={isDisabled}
        className={`w-full flex items-center justify-between p-4 ${
          isDisabled
            ? 'cursor-not-allowed'
            : 'hover:bg-gray-50 cursor-pointer'
        }`}
      >
        <div className="text-left flex-1">
          <div className={`font-medium ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
            Section #{section.order + 1}
            {section.title && ` - ${section.title}`}
            {isDisabled && (
              <span className="ml-2 text-xs font-normal text-gray-400 italic">
                (Section désactivée - visible uniquement sur le site)
              </span>
            )}
          </div>
        </div>
        {!isDisabled && <Pencil className="w-4 h-4 text-gray-400" />}
        {isDisabled && (
          <span className="text-xs text-gray-400 bg-gray-200 px-2 py-1 rounded">
            Hors ligne
          </span>
        )}
      </button>

      {isExpanded && (
        <form onSubmit={handleSubmit(onSubmit)} className="p-4 border-t space-y-4">
          {/* Type de section caché - pas besoin de le montrer à l'utilisateur */}
          <input type="hidden" {...register('type')} />

          <div className="space-y-2">
            <Label htmlFor={`section-title-${section.id}`}>Titre</Label>
            <Input
              id={`section-title-${section.id}`}
              {...register('title')}
              className={errors.title ? 'border-red-500' : ''}
            />
            {errors.title && (
              <p className="text-sm text-red-600">{errors.title.message}</p>
            )}
          </div>

          {/* Masquer le contenu pour les sections qui n'en ont pas besoin */}
          {!['ambiance'].includes(sectionType) && (
            <div className="space-y-2">
              <Label htmlFor={`section-content-${section.id}`}>Contenu</Label>
              <Textarea
                id={`section-content-${section.id}`}
                {...register('content')}
                rows={4}
                placeholder="Contenu de la section..."
                className={errors.content ? 'border-red-500' : ''}
              />
              {errors.content && (
                <p className="text-sm text-red-600">{errors.content.message}</p>
              )}
            </div>
          )}

          {/* Conditional Data Editor based on section type */}
          {sectionType === 'text_image' ? (
            <TextImageDataEditor
              value={sectionData}
              onChange={(data) => setValue('data', data)}
            />
          ) : sectionType === 'benefits' ? (
            <BenefitsDataEditor
              value={sectionData}
              onChange={(data) => setValue('data', data)}
            />
          ) : sectionType === 'services' || sectionType === 'prestations' ? (
            <PrestationsDataEditor
              value={sectionData}
              onChange={(data) => setValue('data', data)}
            />
          ) : sectionType === 'session_process' || sectionType === 'process' ? (
            <SessionProcessDataEditor
              value={sectionData}
              onChange={(data) => setValue('data', data)}
            />
          ) : sectionType === 'faq' ? (
            <FAQDataEditor
              value={sectionData}
              onChange={(data) => setValue('data', data)}
            />
          ) : sectionType === 'approach' ? (
            <ApproachDataEditor
              value={sectionData}
              onChange={(data) => setValue('data', data)}
            />
          ) : sectionType === 'values' ? (
            <ValuesDataEditor
              value={sectionData}
              onChange={(data) => setValue('data', data)}
            />
          ) : sectionType === 'ambiance' ? (
            <AmbianceDataEditor
              value={sectionData}
              onChange={(data) => setValue('data', data)}
            />
          ) : sectionType === 'mosaic_gallery' ? (
            <MosaicGalleryDataEditor
              value={sectionData}
              onChange={(data) => setValue('data', data)}
            />
          ) : sectionType === 'testimonials' ? (
            // Témoignages : Seuls le titre et le contenu sont modifiables
            // Les témoignages sont gérés via la page Témoignages dédiée
            <div className="rounded-md bg-blue-50 p-4">
              <p className="text-sm text-blue-800">
                <strong>Note :</strong> Les témoignages sont gérés via la page "Témoignages" du menu.
                Seuls le titre et le texte d'introduction de cette section sont modifiables ici.
              </p>
            </div>
          ) : sectionType === 'blog' ? (
            <BlogDataEditor
              value={sectionData}
              onChange={(data) => setValue('data', data)}
            />
          ) : sectionType === 'cta' ? (
            // CTA : Seuls le titre et le contenu sont modifiables
            <div className="rounded-md bg-amber-50 p-4">
              <p className="text-sm text-amber-800">
                <strong>Note :</strong> Pour la section CTA (Call-to-Action), seuls le titre et le contenu sont modifiables.
                Les boutons d'action sont configurés automatiquement.
              </p>
            </div>
          ) : sectionType === 'hero' ? (
            <div className="rounded-md bg-purple-50 p-4">
              <p className="text-sm text-purple-800">
                <strong>Note :</strong> Pour la section Hero, le titre, le contenu et l'image de fond sont modifiables. L'image s'affiche en arrière-plan avec parallax.
              </p>
            </div>
          ) : sectionType === 'massages_list' ? (
            <div className="rounded-md bg-blue-50 p-4">
              <p className="text-sm text-blue-800">
                <strong>Note :</strong> Les massages sont gérés via le menu "Massages" de l'administration. Ils sont affichés automatiquement par catégorie sur cette page.
              </p>
            </div>
          ) : sectionType === 'soins_list' ? (
            <div className="rounded-md bg-blue-50 p-4">
              <p className="text-sm text-blue-800">
                <strong>Note :</strong> Les soins énergétiques sont gérés via le menu "Soins" de l'administration. Ils sont affichés automatiquement avec filtres par catégorie.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor={`section-data-${section.id}`}>Configuration JSON (Optionnel)</Label>
              <Textarea
                id={`section-data-${section.id}`}
                value={sectionData ? JSON.stringify(sectionData, null, 2) : ''}
                onChange={(e) => {
                  try {
                    const parsed = e.target.value ? JSON.parse(e.target.value) : null;
                    setValue('data', parsed);
                  } catch {
                    // Invalid JSON, ignore
                  }
                }}
                rows={6}
                placeholder={'{\n  "key": "value"\n}'}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                Configuration JSON pour cette section (ex: featured IDs, cards, etc.)
              </p>
            </div>
          )}

          {/* Afficher le champ image seulement pour les types qui en ont besoin */}
          {['hero', 'text_image', 'gallery', 'ambiance'].includes(sectionType) && (
            <MediaPicker
              value={imageUrl}
              onChange={(url) => setValue('imageUrl', url)}
              label="Image de la section"
            />
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onToggle}>
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Sauvegarder
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
